import { DevConfig } from '@config/end.dev.config';
import { LocalPaymentConfig } from '@config/end.local-payment.config';
import { LocalConfig } from '@config/end.local.config';
import { ProdConfig } from '@config/end.prod.config';
import { QaConfig } from '@config/end.qa.config';
import { StagingConfig } from '@config/end.staging.config';
import { EnvConfig } from '@config/EnvConfig';

import type { AppEnv } from '@config/EnvConfig';
import type { TSoftphoneConfig } from '@states/softphone/softphoneAtom';

import packageJson from '../../package.json' with { type: 'json' };

export type AppConfig = {
  apiKeyGoogleMaps: string;
  appEnv: AppEnv;
  azure: {
    appInsightsConnectionString?: string;
    appInsightsKey?: string;
    authority: string;
    clientId: string;
    tenantId: string;
  };
  gatewayUrl: string;
  paymentUrl: string;
  softphoneIntegration: TSoftphoneConfig;
  defaultApiTimeout?: number;
  sentryIngestionEndpoint: string;
};

function getAppConfig(): AppConfig & { version: string } {
  const version = packageJson.version;

  switch (EnvConfig.appEnv) {
    case 'local':
      return { ...LocalConfig, version };
    case 'local-payment':
      return { ...LocalPaymentConfig, version };
    case 'development':
      return { ...DevConfig, version };
    case 'qa':
      return { ...QaConfig, version };
    case 'staging':
      return { ...StagingConfig, version };
    case 'production':
      return { ...ProdConfig, version };
    default:
      throw new Error(`${EnvConfig.appEnv} is not a valid environment`);
  }
}

const AppConfig = getAppConfig();

/**
 * Share of client errors sent to Sentry, per environment. A rate of 0 keeps
 * Sentry uninitialized rather than initializing it and dropping everything.
 *
 * QA sits below production because its automated suites and training sessions
 * repeat the same failures all day.
 */
const SENTRY_ERROR_SAMPLE_RATE: Record<AppEnv, number> = {
  local: 0,
  'local-payment': 0,
  development: 0,
  qa: 0.05,
  staging: 0.1,
  production: 0.1,
};

const sentryErrorSampleRate = SENTRY_ERROR_SAMPLE_RATE[AppConfig.appEnv];

const isSentryEnabled = sentryErrorSampleRate > 0;

export { AppConfig, EnvConfig, isSentryEnabled, sentryErrorSampleRate };
